import { useState, useRef, useEffect } from "react";
import Loader from "../Loader/Loader";
import ArticleSubHeader from "./ArticleSubHeader";
import ArticleListItem from "./ArticleListItem";
import { useSelector, useDispatch } from "react-redux";
import { getArticleListing } from "../../../core/actionCreators/articleActionCreator";
import CommonPagination from "../CommonPagination/CommonPagination";
import { GET_ARTICLE_PAGE_SIZE } from "../../../utils/constants";
import queryString from "query-string";
import { removeEmptyStringKeys } from "../../../utils/helper";
import { getArticleListingService } from "../../../core/services/articleServices";
import toast from "react-hot-toast";

const EMPTY_FILTERS = {
    articleTitle: "",
    authorName: "",
    categoryCode: "",
    friendlyUrl: "",
    articleType: "",
}

const BASE_CRITERIA = {
    pageSize: GET_ARTICLE_PAGE_SIZE,
    layoutCode: "",
    pageType: '2,3,4',
}

const FILTER_DEBOUNCE_MS = 600;

const ARTICLE_TYPES = [
    { value: "STORE_PAGE", label: "Store Page" },
    { value: "ONLINE_ARTICLE", label: "Online Article" },
    { value: "SUPPLIER_BLOG", label: "Supplier Blog" },
    { value: "SUPPLIER_PAGE", label: "Supplier Page" },
]

const INPUT_CLASS = "border border-gray-300 h-8 rounded-sm focus:outline-none focus:border-blue-400 px-1.5 text-xs w-full";

const ArticleList = () => {
    const dispatch = useDispatch();
    const { isLoading, articleListing } = useSelector((s: any) => s.articleStore)

    const [filters, setFilters] = useState({ ...EMPTY_FILTERS } as any);
    const [pageNumber, setPageNumber] = useState(0);

    const [isDownloading, setIsDownloading] = useState(false);

    // Typing should settle before firing a request; paging should not wait.
    const isFilterChangeRef = useRef(false);

    useEffect(() => {
        const delay = isFilterChangeRef.current ? FILTER_DEBOUNCE_MS : 0;
        const timeout = setTimeout(() => {
            isFilterChangeRef.current = false;
            dispatch(getArticleListing(
                queryString.stringify(removeEmptyStringKeys({ ...BASE_CRITERIA, ...filters, pageNumber })),
                null
            ));
        }, delay);
        return () => clearTimeout(timeout);
    }, [filters, pageNumber, dispatch]);

    // A changed filter re-runs the search from the first page — keeping the old
    // page number would land the user on an empty tail of a shorter result set.
    const onFilterChange = (key: string, value: string) => {
        isFilterChangeRef.current = true;
        setFilters({ ...filters, [key]: value });
        setPageNumber(0);
    }

    const hasActiveFilters = Object.keys(EMPTY_FILTERS).some((key) => !!filters[key]);

    const clearFilters = () => {
        isFilterChangeRef.current = false;
        setFilters({ ...EMPTY_FILTERS });
        setPageNumber(0);
    }

    // CommonPagination counts pages from 1, the API counts them from 0.
    const onPageChange = (page: any) => {
        setPageNumber(Math.max(0, Number(page) - 1));
    }

    const downloadFileClickHandler = () => {
        setIsDownloading(true);
        // User requested capped at 1000
        const parsedSearchCriteria = removeEmptyStringKeys({ ...BASE_CRITERIA, ...filters, pageSize: 1000, pageNumber: 0 });
        const stringifiedSearchCriteria = queryString.stringify(parsedSearchCriteria);

        getArticleListingService(stringifiedSearchCriteria).then(({ data }: any) => {
            if (data && data.data && data.data.length > 0) {
                const header = Object.keys(data.data[0]).join(',');
                const rows = data.data.map((obj: any) => Object.values(obj).map((val: any) => {
                    let str = val !== null && val !== undefined ? String(val) : '';
                    if (str.includes(',') || str.includes('\n') || str.includes('"')) {
                        str = `"${str.replace(/"/g, '""')}"`;
                    }
                    return str;
                }).join(',')).join('\n');
                const csvContent = `${header}\n${rows}`;

                const blob = new Blob([csvContent], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'articles_dump.csv';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                toast.success('Downloaded successfully');
            } else {
                toast.error('No data found to download');
            }
        }).catch((err: any) => {
            console.log('downloadFileClickHandler error:', err);
            toast.error('Something went wrong');
        }).finally(() => {
            setIsDownloading(false);
        });
    };

    return (
        <>
            <ArticleSubHeader onDownloadClick={downloadFileClickHandler} isDownloading={isDownloading} />
            <div className="mt-2.5 bg-white px-4 py-3 flex flex-wrap items-end gap-x-4 gap-y-3">
                <div className="w-[240px]">
                    <label className="block text-[11px] text-gray-600 mb-1">Title</label>
                    <input
                        type="text"
                        placeholder="Search title"
                        value={filters.articleTitle}
                        onChange={(e) => onFilterChange("articleTitle", e.target.value)}
                        className={INPUT_CLASS}
                    />
                </div>
                <div className="w-[180px]">
                    <label className="block text-[11px] text-gray-600 mb-1">Author</label>
                    <input
                        type="text"
                        placeholder="Search author"
                        value={filters.authorName}
                        onChange={(e) => onFilterChange("authorName", e.target.value)}
                        className={INPUT_CLASS}
                    />
                </div>
                <div className="w-[150px]">
                    <label className="block text-[11px] text-gray-600 mb-1">Category Code</label>
                    <input
                        type="text"
                        placeholder="Category code"
                        value={filters.categoryCode}
                        onChange={(e) => onFilterChange("categoryCode", e.target.value)}
                        className={INPUT_CLASS}
                    />
                </div>
                <div className="w-[240px]">
                    <label className="block text-[11px] text-gray-600 mb-1">Friendly Url</label>
                    <input
                        type="text"
                        placeholder="Search friendly url"
                        value={filters.friendlyUrl}
                        onChange={(e) => onFilterChange("friendlyUrl", e.target.value)}
                        className={INPUT_CLASS}
                    />
                </div>
                <div className="w-[160px]">
                    <label className="block text-[11px] text-gray-600 mb-1">Flag</label>
                    <select
                        value={filters.articleType}
                        onChange={(e) => onFilterChange("articleType", e.target.value)}
                        className="bg-white border border-gray-300 h-8 w-full focus:outline-none focus:border-blue-400 px-1.5 rounded-sm text-xs"
                    >
                        <option value="">All</option>
                        {ARTICLE_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                    </select>
                </div>
                <button
                    onClick={clearFilters}
                    disabled={!hasActiveFilters}
                    className="h-8 px-4 rounded text-xs font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Clear Filters
                </button>
            </div>
            <div className="mt-2.5 campaign-list bg-white mb-[54px]">
                <table className="w-full">
                    <thead className="bg-[#343434]">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[6%]">ID</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[22%]">Title</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[12%]">Author</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[10%]">Category Code</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[20%]">Friendly Url</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[10%]">Updated At</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[10%]">Published At</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[8%]">Flag</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[8%]">Status</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[6%]">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={10} className="py-12">
                                    <div className="flex flex-col items-center justify-center space-y-2">
                                        <Loader />
                                        <span className="text-xs text-gray-500">Loading articles...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            <ArticleListItem data={articleListing?.data || []} />
                        )}
                    </tbody>
                </table>
            </div>
            {articleListing?.data && (<CommonPagination data={articleListing} PAGE_SIZE={GET_ARTICLE_PAGE_SIZE} onPageChange={onPageChange} />)}
        </>
    )

}
export default ArticleList;
